/**
 * The bell: what a buyer or mill is told, read here rather than only by
 * loading the screen the notification is about.
 *
 * Two layers, deliberately independent. The list below always works, straight
 * from `GET /notifications`, refreshed the same way every other screen here
 * is (`useRefreshOnFocus`, in ui.tsx) plus a light poll while the tab stays
 * open — good enough for a tool used on a phone between other jobs. Push is
 * additive: turning it on (see push.ts) means the same notifications also
 * reach the phone when this tab is not the one in front, but nothing here
 * depends on it being on, and the toggle simply does not appear where it
 * cannot work — no browser support, or this deployment has no VAPID keypair.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from './api/client'
import { disablePush, enablePush, pushState as readPushState, type PushState } from './push'
import { Button, Empty, useRefreshOnFocus } from './ui'

interface NotificationRow {
  id: string
  kind: string
  title: string
  body: string | null
  link: string | null
  readAt: string | null
  createdAt: string
}

/** Not a substitute for push — just often enough that a tab left open still catches up on its own. */
const POLL_MS = 45_000

export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<NotificationRow[]>([])
  const [unread, setUnread] = useState(0)
  const [push, setPush] = useState<PushState>('unsupported')

  const load = async () => {
    try {
      const result = await api.get<{ notifications: NotificationRow[]; unread: number }>(
        '/notifications?limit=20',
      )
      setRows(result.notifications)
      setUnread(result.unread)
    } catch {
      // Staying at the last known count is a small failure worth ignoring
      // rather than putting an error banner over someone's whole day.
    }
  }

  useEffect(() => {
    void load()
    void readPushState().then(setPush)
    const timer = window.setInterval(() => void load(), POLL_MS)
    return () => window.clearInterval(timer)
  }, [])
  useRefreshOnFocus(() => void load())

  const markAllRead = async () => {
    await api.post('/notifications/read-all')
    const now = new Date().toISOString()
    setRows((current) => current.map((row) => ({ ...row, readAt: row.readAt ?? now })))
    setUnread(0)
  }

  const openRow = (row: NotificationRow) => {
    if (!row.readAt) {
      const now = new Date().toISOString()
      setRows((current) => current.map((r) => (r.id === row.id ? { ...r, readAt: now } : r)))
      setUnread((count) => Math.max(0, count - 1))
      void api.post(`/notifications/${row.id}/read`)
    }
    setOpen(false)
    if (row.link) navigate(row.link)
  }

  const togglePush = async () => {
    if (push === 'on') {
      await disablePush()
      setPush('off')
    } else {
      setPush(await enablePush())
    }
  }

  return (
    <div className="bell">
      <button
        type="button"
        className="button button-quiet theme-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        🔔
        {unread > 0 ? <span className="bell-badge">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <>
          <div className="drawer-backdrop" onClick={() => setOpen(false)} />
          <nav className="drawer drawer-right">
            <div className="drawer-head">
              <strong>Notifications</strong>
              <Button variant="quiet" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>

            {push !== 'unsupported' ? (
              <button type="button" className="drawer-item" onClick={() => void togglePush()}>
                <span>{push === 'on' ? 'Push notifications' : 'Turn on push notifications'}</span>
                <span className="drawer-count">{push === 'on' ? 'On' : 'Off'}</span>
              </button>
            ) : null}

            {unread > 0 ? (
              <button type="button" className="drawer-item" onClick={() => void markAllRead()}>
                <span>Mark all read</span>
                <span className="drawer-count">{unread}</span>
              </button>
            ) : null}

            {rows.length === 0 ? <Empty>Nothing yet.</Empty> : null}
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                className={`drawer-item${row.readAt ? '' : ' notif-unread'}`}
                onClick={() => openRow(row)}
              >
                <span>{row.title}</span>
                {!row.readAt ? <span className="notif-dot" aria-hidden="true" /> : null}
              </button>
            ))}
          </nav>
        </>
      ) : null}
    </div>
  )
}
